import { Button, Paper, Stack, Typography, Chip, Box } from "@mui/material";

import { CreateAppointmentDialog } from "../../pages/schedulemanager/components/creatappointmentdialog";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { Dispatch, SetStateAction, useEffect, useState, useMemo } from "react";

import { DateFilterType } from "../../utils/interfaces";

import { ScheduleSelectionCards } from "./ScheduleSelectionCards";
import { ViewHeader } from "./components/ViewHeader";
import { useContractContext } from "@/contexts/ContractContext";
import {
  AccessTime,
  CheckCircle,
  AutoAwesome,
  Autorenew,
  CalendarMonth,
  MonitorHeart,
  WhatsApp,
  Timeline,
} from "@mui/icons-material";
import ScheduleFluxZapShortcutsPanel from "./ScheduleFluxZapShortcutsPanel";
import ScheduleDocumentReissuePanel from "./documentReissue/ScheduleDocumentReissuePanel";
import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import { Link } from "react-router-dom";
import { useDatetime } from "@/utils/useDatetime";
import { useLocationContext } from "@/contexts/LocationContext";
import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetAppointment, GetSlot } from "@/services/fhir";
import { AppointmentsViewer } from "./AppointmentsViewer";
import { EvolutionFromScheduleDialog } from "./evolution/EvolutionFromScheduleDialog";
import { EVOLUTION_TYPE_SERVICE } from "./evolution/constants";
import { AvailableSlotsViewer } from "./AvailableSlotsViewer";
import { Schedule } from "fhir/r5";
import { useViewContext } from "@/contexts/ViewContext";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import {
  filterAppointmentBundleByPractitionerRefs,
  getAssociationCnpjDigitsFromRoleResource,
  onlyDigitsAssociation,
  practitionerRefsSharingAssociationCnpj,
} from "@/utils/practitionerRoleAssociationScope";

interface IProps {
  schedules?: any;
  dateFilter: Dayjs;
  setDateFilter: Dispatch<SetStateAction<Dayjs>>;
  filterType: DateFilterType;
  setFilterType: Dispatch<SetStateAction<DateFilterType>>;
  setRefetch?: Dispatch<SetStateAction<boolean>> | (() => void);
  refetch: boolean;
}

export function ProfessionalSchedule({
  schedules,
  dateFilter,
  filterType,
  setDateFilter,
  setFilterType,

  setRefetch,
  refetch,
}: IProps) {
  const { user, access } = useSessionContext();
  const { contractPostAttendance } = usePostAttendanceContext();
  const { viewType, availableRoles } = useViewContext();
  // Mesma regra do atalho em PatientSearchActions (só practitioner reemite)
  const isPractitioner = availableRoles?.includes("practitioner");
  // PA movido para /prontoatendimento (menu dedicado)
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [loading, setLoading] = useState(true);

  // Define as datas limites para cada tipo de visualização
  const today = dayjs().startOf("day");

  // Hook para controle dos filtros administrativos (usado apenas quando viewType === 'admin')
  const {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    resetFilters,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  } = useAdministrativeFilters({
    onChange: (filters) => {
      if (viewType === "admin") {
        setIsSearchActive(true);
      }
    },
  });

  // Secretariado só com vínculo de CNPJ (extensão): lista de profissionais no filtro admin fica restrita ao mesmo CNPJ.
  const practitionerRoleScoped = useMemo(() => {
    const scope = onlyDigitsAssociation(
      access?.managerAssociationScopeCnpjDigits || "",
    );
    if (!scope || !practitionerRole?.length) return practitionerRole;
    return practitionerRole.filter(
      (e) => getAssociationCnpjDigitsFromRoleResource(e?.resource) === scope,
    );
  }, [practitionerRole, access?.managerAssociationScopeCnpjDigits]);

  const [createAppointmentDialogOpen, setCreateAppointmentDialogOpen] =
    useState<boolean>(false);
  const [isWalkin, setIsWalkin] = useState(false);
  const [isShortCutAppointment, setIsShortCutAppointment] = useState(false);
  const [evolutionDialogOpen, setEvolutionDialogOpen] = useState(false);
  // CTA da agenda clínica: abre CreateAppointmentDialog já como Evolução.
  const [isEvolutionShortcut, setIsEvolutionShortcut] = useState(false);
  // Estado para controlar a visualização atual (agendados, completados, disponíveis, FluxZap, pacientes)
  const [selectedView, setSelectedView] = useState("scheduled");

  const {
    formatDateToISO,
    formatDateTimeToISO,
    formatDateTimeWithThresholdToISO,
    isDateFilterTypeDay,
    isSameYearAndMonthAsToday,
  } = useDatetime;
  const { location: contextLocation, locationLoading: contextLocationLoading } =
    useLocationContext();

  const handleViewChange = (view: string) => {
    setRefetch?.((prev: boolean) => !prev);
    if (view === "scheduled" && dateFilter.isBefore(today, "day")) {
      setDateFilter(today);
    }
    if (view === "available" && dateFilter.isBefore(today, "day")) {
      setDateFilter(today);
    }
    if (view === "completed" && dateFilter.isAfter(today, "day")) {
      setDateFilter(today);
    }
    setSelectedView(view);
  };

  // Handler para mudança no filtro de status (seleção única)
  const handleStatusFilterChange = (status: string) => {
    setSelectedStatus(status);
  };

  // Função para obter o label do status em português
  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      booked: "Confirmadas",
      pending: "Pendentes",
      proposed: "Propostas",
    };
    return labels[status] || status;
  };

  // Função para obter a cor do status
  const getStatusColor = (status: string) => {
    const colors: Record<string, "primary" | "warning" | "info"> = {
      booked: "primary",
      pending: "warning",
      proposed: "info",
    };
    return colors[status] || "primary";
  };

  const [nextAppointments, setNextAppointments] =
    useState<fhir5.Bundle<fhir5.Appointment>>();

  const [completedAppointments, setCompletedAppointments] =
    useState<fhir5.Bundle<fhir5.Appointment>>();

  const [availableSlots, setAvailableSlots] =
    useState<fhir5.Bundle<fhir5.Slot>>();

  // Estado para controlar o filtro de status das próximas consultas (seleção única)
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  // Conta appointments por status
  const statusCounts = useMemo(() => {
    if (!nextAppointments?.entry) {
      return { all: 0, booked: 0, pending: 0, proposed: 0 };
    }

    const counts = { all: 0, booked: 0, pending: 0, proposed: 0 };

    nextAppointments.entry.forEach((entry: any) => {
      if (entry.resource?.resourceType === "Appointment") {
        const status = entry.resource?.status;
        counts.all++;
        if (status === "booked") counts.booked++;
        if (status === "pending") counts.pending++;
        if (status === "proposed") counts.proposed++;
      }
    });

    return counts;
  }, [nextAppointments]);

  // Filtra os appointments baseado no status selecionado
  const filteredNextAppointments = useMemo(() => {
    if (!nextAppointments?.entry) return nextAppointments;

    // Se "all" estiver selecionado, retorna todos
    if (selectedStatus === "all") {
      return nextAppointments;
    }

    const filteredEntries = nextAppointments.entry.filter((entry: any) => {
      if (entry.resource?.resourceType === "Appointment") {
        const appointmentStatus = entry.resource?.status;
        return appointmentStatus === selectedStatus;
      }
      return true;
    });

    return {
      ...nextAppointments,
      entry: filteredEntries,
    };
  }, [nextAppointments, selectedStatus]);

  const cards = [
    {
      id: "scheduled",
      title: "Próximas consultas",
      icon: CalendarMonth,
      content: (
        <Stack direction="column" width="100%" spacing={1}>
          <ViewHeader
            title="Próximas consultas"
            dateFilter={dateFilter}
            filterType={filterType}
            setDateFilter={setDateFilter}
            setFilterType={setFilterType}
            showAdminFilters={viewType === "admin"}
            minDate={today} // Próximas consultas: mínimo é hoje
            adminFiltersProps={{
              selectedLocation,
              selectedPractitioner,
              onLocationChange: handleLocationChange,
              onPractitionerChange: handlePractitionerChange,
              location,
              locationLoading,
              practitionerRole: practitionerRoleScoped,
              practitioner,
              practitionerRoleLoading,
              onResetFilters: resetFilters,
              setIsSearchActive,
            }}
            resetButtonProps={{ tooltip: "Atualizar / Limpar Filtros" }}
          />

          {/* Filtros de status para próximas consultas */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontWeight: 500, mr: 1 }}
              >
                Status:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {/* Botão "Todos" */}
                <Chip
                  label={`Todos (${statusCounts.all})`}
                  onClick={() => handleStatusFilterChange("all")}
                  clickable
                  variant={selectedStatus === "all" ? "filled" : "outlined"}
                  color={selectedStatus === "all" ? "primary" : "default"}
                  sx={{
                    fontWeight: selectedStatus === "all" ? 600 : 400,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      bgcolor:
                        selectedStatus === "all"
                          ? "primary.dark"
                          : "action.hover",
                      transform: "translateY(-1px)",
                      boxShadow: 1,
                    },
                  }}
                />
                {/* Botões de status individuais */}
                {["booked", "pending", "proposed"].map((status) => (
                  <Chip
                    key={status}
                    label={`${getStatusLabel(status)} (${
                      statusCounts[status as keyof typeof statusCounts]
                    })`}
                    onClick={() => handleStatusFilterChange(status)}
                    clickable
                    variant={selectedStatus === status ? "filled" : "outlined"}
                    color={
                      selectedStatus === status
                        ? getStatusColor(status)
                        : "default"
                    }
                    sx={{
                      fontWeight: selectedStatus === status ? 600 : 400,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        bgcolor:
                          selectedStatus === status
                            ? `${getStatusColor(status)}.dark`
                            : "action.hover",
                        transform: "translateY(-1px)",
                        boxShadow: 1,
                      },
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>

          <AppointmentsViewer
            bundle={filteredNextAppointments}
            loading={loading}
            setRefetch={setRefetch}
            filterType={filterType}
          />
        </Stack>
      ),
    },
    {
      id: "available",
      title: "Horários disponíveis",
      icon: AccessTime,
      content: (
        <Stack direction="column" width="100%" spacing={1}>
          <ViewHeader
            title="Horários Disponíveis"
            dateFilter={dateFilter}
            filterType={filterType}
            setDateFilter={setDateFilter}
            setFilterType={setFilterType}
            showAdminFilters={viewType === "admin"}
            minDate={today} // Horários disponíveis: mínimo é hoje
            adminFiltersProps={
              viewType === "admin"
                ? {
                    selectedLocation,
                    selectedPractitioner,
                    onLocationChange: handleLocationChange,
                    onPractitionerChange: handlePractitionerChange,
                    location,
                    locationLoading,
                    practitionerRole: practitionerRoleScoped,
                    practitioner,
                    practitionerRoleLoading,
                    onResetFilters: resetFilters,
                    setIsSearchActive,
                  }
                : undefined
            }
            resetButtonProps={{ tooltip: "Atualizar / Limpar Filtros" }}
          />

          <AvailableSlotsViewer
            bundle={availableSlots}
            schedule={schedules}
            loading={loading}
            setRefetch={setRefetch}
          />
        </Stack>
      ),
    },
    {
      id: "completed",
      title: "Consultas realizadas",
      icon: CheckCircle,
      content: (
        <Stack direction="column" width="100%" spacing={1}>
          <ViewHeader
            title="Consultas Realizadas"
            dateFilter={dateFilter}
            filterType={filterType}
            setDateFilter={setDateFilter}
            setFilterType={setFilterType}
            showAdminFilters={viewType === "admin"}
            maxDate={today} // Consultas realizadas: máximo é hoje
            adminFiltersProps={
              viewType === "admin"
                ? {
                    selectedLocation,
                    selectedPractitioner,
                    onLocationChange: handleLocationChange,
                    onPractitionerChange: handlePractitionerChange,
                    location,
                    locationLoading,
                    practitionerRole: practitionerRoleScoped,
                    practitioner,
                    practitionerRoleLoading,
                    onResetFilters: resetFilters,
                    setIsSearchActive,
                  }
                : undefined
            }
            resetButtonProps={{ tooltip: "Atualizar / Limpar Filtros" }}
          />

          <AppointmentsViewer
            loading={loading}
            bundle={completedAppointments}
            setRefetch={setRefetch}
            filterType={filterType}
          />
        </Stack>
      ),
    },
    // Card entre Consultas realizadas e FluxZap — fluxo inline (sem Dialog)
    ...(isPractitioner
      ? [
          {
            id: "reissue-documents",
            title: "Reemitir Documentos",
            icon: Autorenew,
            content: (
              <ScheduleDocumentReissuePanel
                isActive={selectedView === "reissue-documents"}
              />
            ),
          },
        ]
      : []),
    {
      id: "post",
      title: "FluxZap",
      icon: WhatsApp,
      content: (
        <Stack direction="column" width="100%" spacing={1}>
          <ViewHeader title="FluxZap" titleOnly />

          {contractPostAttendance ? (
            <ScheduleFluxZapShortcutsPanel />
          ) : (
            <Stack>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{
                  backgroundColor: "neutral100.main",
                  borderRadius: 2,
                  p: 3,
                  border: "1px dashed",
                  borderColor: "neutral300.main",
                }}
              >
                <MonitorHeart sx={{ fontSize: 40, color: "primary.main" }} />
                <Stack spacing={1}>
                  <Typography variant="body1" fontWeight={500}>
                    Integre o número de WhatsApp da sua clínica e faça o
                    acompanhamento da jornada clínica de seu paciente de forma
                    integrada.
                  </Typography>
                  <Typography variant="body2" color="neutral700.main">
                    Gerencie medicamentos, exames e o acompanhamento
                    pós-consulta dos seus pacientes
                  </Typography>
                  <Button
                    component={Link}
                    to="/extensions"
                    variant="contained"
                    startIcon={<AutoAwesome />}
                    sx={{
                      mt: 1,
                      textTransform: "capitalize",
                      width: "fit-content",
                    }}
                  >
                    Ativar extensão
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Stack>
      ),
    },
  ];

  // Hook para buscar appointments
  useEffect(() => {
    async function fetchData() {
      if (!contextLocation?.length || contextLocationLoading) return;

      const contextLocationId = contextLocation
        ?.map(
          (element: fhir5.BundleEntry<fhir5.Location>) => element.resource?.id,
        )
        ?.join(",");

      const schedulesIds = schedules
        ?.map((element: Schedule) => element?.id)
        ?.join(",");

      const auxSlotFilter = schedules
        ?.filter((element: Schedule) => {
          const scheduleLocation = element?.actor?.find((actor) =>
            actor?.reference?.includes("Location"),
          );
          const schedulePractitioner = element?.actor?.find((actor) =>
            actor?.reference?.includes("Practitioner"),
          );

          const auxLocationFilter =
            selectedLocation !== "all"
              ? scheduleLocation?.reference?.split("/")[1] === selectedLocation
              : true;
          const auxPractitionerFilter =
            selectedPractitioner !== "all"
              ? schedulePractitioner?.reference?.split("-")?.[1] ===
                selectedPractitioner?.split("-").pop()
              : true;

          return auxLocationFilter && auxPractitionerFilter;
        })
        ?.map((element: Schedule) => element?.id)
        ?.join(",");

      // Define o professional actor baseado no viewType e filtros
      const professionalActor =
        viewType === "user"
          ? `Practitioner/${oids.cpf}-${user.username}`
          : selectedPractitioner !== "all"
            ? `Practitioner/${oids.cpf}-${selectedPractitioner?.split("-").pop()}`
            : undefined;

      // Define o location actor baseado no viewType e filtros
      const locationId =
        viewType === "admin" && selectedLocation !== "all"
          ? selectedLocation
          : contextLocationId;

      // Calcula a data de início baseado no tipo de filtro
      const startDate =
        isDateFilterTypeDay(filterType) || isSameYearAndMonthAsToday(dateFilter)
          ? formatDateToISO(dateFilter)
          : filterType === "week"
            ? formatDateToISO(dateFilter.startOf("week"))
            : formatDateToISO(dateFilter.startOf("month"));

      // Calcula a data de início para slots disponíveis
      const startDateSlot =
        isDateFilterTypeDay(filterType) || isSameYearAndMonthAsToday(dateFilter)
          ? dateFilter.isSame(dayjs(), "day")
            ? formatDateTimeWithThresholdToISO(dayjs()) // Se for hoje, usa a data e hora atual com threshold de 15min
            : formatDateToISO(dateFilter.startOf("day")) // Se for outro dia, usa início do dia
          : filterType === "week"
            ? formatDateToISO(dateFilter.startOf("week"))
            : formatDateToISO(dateFilter.startOf("month"));

      // Calcula a data de fim baseado no tipo de filtro
      const endDate = isDateFilterTypeDay(filterType)
        ? formatDateToISO(dateFilter)
        : filterType === "week"
          ? formatDateToISO(dateFilter.endOf("week"))
          : formatDateToISO(dateFilter.endOf("month"));

      async function getAvailableSlots() {
        if (viewType === "admin" && !auxSlotFilter?.length) return [];
        return GetSlot({
          schedule: viewType === "admin" ? auxSlotFilter : schedulesIds,
          status: ["free"],
          sort: "start",
          startDate: startDateSlot,
          endDate,
        });
      }
      setLoading(true);
      try {
        const [
          appointmentResponse,
          completedAppointmentResponse,
          availableSlots,
        ] = await Promise.all([
          GetAppointment({
            scheduling: true,
            includePatient: true,
            includeLocation: true,
            status: ["booked", "pending", "proposed"],
            actor: locationId,
            startDate,
            endDate,
            sort: "date",
            professionalActor: professionalActor,
          }),
          GetAppointment({
            scheduling: true,
            includePatient: true,
            includeLocation: true,
            status: ["fulfilled"],
            actor: locationId,
            startDate,
            endDate,
            sort: "date",
            professionalActor: professionalActor,
          }),
          getAvailableSlots(),
        ]);

        const scopeDigits = access?.managerAssociationScopeCnpjDigits;
        const allowedPractitionerRefs = practitionerRefsSharingAssociationCnpj(
          practitionerRole,
          scopeDigits,
        );
        let nextBundle = appointmentResponse;
        let completedBundle = completedAppointmentResponse;
        if (scopeDigits && allowedPractitionerRefs.size > 0) {
          nextBundle = filterAppointmentBundleByPractitionerRefs(
            appointmentResponse,
            allowedPractitionerRefs,
          );
          completedBundle = filterAppointmentBundleByPractitionerRefs(
            completedAppointmentResponse,
            allowedPractitionerRefs,
          );
        } else if (scopeDigits && allowedPractitionerRefs.size === 0) {
          nextBundle = appointmentResponse
            ? { ...appointmentResponse, entry: [] }
            : appointmentResponse;
          completedBundle = completedAppointmentResponse
            ? { ...completedAppointmentResponse, entry: [] }
            : completedAppointmentResponse;
        }

        setNextAppointments(nextBundle);
        setCompletedAppointments(completedBundle);
        setAvailableSlots(availableSlots);
      } catch (error) {
        console.error("Erro ao buscar appointments:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [
    dateFilter,
    filterType,
    user.username,
    access.roles,
    access.managerAssociationScopeCnpjDigits,
    practitionerRole,
    formatDateToISO,
    isDateFilterTypeDay,
    isSameYearAndMonthAsToday,
    location,
    locationLoading,
    refetch,
    // Adiciona dependências dos filtros administrativos quando for admin
    ...(viewType === "admin"
      ? [selectedLocation, selectedPractitioner, isSearchActive]
      : []),
  ]);

  // Visão clínica: secretaria/manager também vê o CTA. Visão profissional: só practitioner.
  const showEvolutionButton = viewType === "admin" || isPractitioner;

  const handleEvolutionClick = () => {
    if (viewType === "admin") {
      setIsEvolutionShortcut(true);
      setCreateAppointmentDialogOpen(true);
      return;
    }
    setEvolutionDialogOpen(true);
  };

  const handleCreateAppointmentDialogClose = (open: boolean) => {
    setCreateAppointmentDialogOpen(open);
    if (!open) setIsEvolutionShortcut(false);
  };

  return (
    <>
      {/* {userPractitioner && currentContract && hasActiveSchedule && !loading && (
        <Button
          size="small"
          onClick={() => {
            setCreateAppointmentDialogOpen(true);
            setIsWalkin(true);
          }}
        >
          Marcar consulta
        </Button>
      )} */}

      <Stack
        spacing={2}
        width="100%"
        maxHeight={"fit-content"}
        sx={{ alignItems: "flex-start" }}
      >
        <ScheduleSelectionCards
          cards={cards}
          selectedView={selectedView}
          onSelectView={handleViewChange}
          endAction={
            showEvolutionButton ? (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Timeline />}
                onClick={handleEvolutionClick}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                Evolução de pacientes
              </Button>
            ) : undefined
          }
        />

        {/* Área de conteúdo com altura máxima e scroll independente para manter header/tabs visíveis */}
        <Stack
          width="100%"
          spacing={1}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            maxHeight: "55vh",
            overflow: "auto",
            "&::-webkit-scrollbar": { width: "0.6em" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "primary700.main",
            },
          }}
        >
          {cards?.find((card) => card.id === selectedView)?.content}
        </Stack>

        {createAppointmentDialogOpen && (
          <CreateAppointmentDialog
            isOpen={createAppointmentDialogOpen}
            setModalOpen={handleCreateAppointmentDialogClose}
            setRefetchDashboard={setRefetch}
            cpfT={""}
            walkin={isWalkin}
            isShortCutAppointment={
              isEvolutionShortcut || isShortCutAppointment
            }
            initialTypeService={
              isEvolutionShortcut ? EVOLUTION_TYPE_SERVICE : undefined
            }
          />
        )}
        {evolutionDialogOpen && (
          <EvolutionFromScheduleDialog
            open={evolutionDialogOpen}
            onClose={() => setEvolutionDialogOpen(false)}
            schedules={schedules}
            onSuccess={() => setRefetch?.((prev: boolean) => !prev)}
          />
        )}
      </Stack>
    </>
  );
}
